import type { Dispatch, SetStateAction } from "react";
import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { EditorState } from "../connectorManagerHelpers";

export const SubstackUnofficialFields = ({
  editor,
  setEditor,
}: {
  editor: EditorState;
  setEditor: Dispatch<SetStateAction<EditorState>>;
}) => (
  <div className="space-y-3">
    <div className="flex items-start gap-2 rounded-md border border-dashed border-amber-500/40 bg-amber-500/10 p-3 text-xs text-foreground">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <div>
        <p className="font-medium">Unofficial — may break without notice</p>
        <p className="mt-1 text-muted-foreground">
          Substack does not publish an official API. Excerptus uses your session
          cookie to push drafts. If Substack changes their internal endpoints,
          dispatches will fail and you&apos;ll need to re-paste a fresh cookie.
          Nothing is auto-published — every push lands as a draft for you to
          review in Substack.
        </p>
      </div>
    </div>

    <div className="space-y-2">
      <Label htmlFor="connector-substack-url">Publication URL</Label>
      <Input
        id="connector-substack-url"
        type="url"
        value={editor.substackPublicationUrl}
        onChange={(e) =>
          setEditor((s) => ({ ...s, substackPublicationUrl: e.target.value }))
        }
        placeholder="https://yourname.substack.com"
      />
      <p className="text-xs text-muted-foreground">
        Your Substack publication&apos;s root URL. Must be <code>https://</code>.
      </p>
    </div>

    <div className="space-y-2">
      <Label htmlFor="connector-substack-cookie">
        Session cookie {editor.connector ? "(leave blank to keep)" : ""}
      </Label>
      <Textarea
        id="connector-substack-cookie"
        value={editor.secretValue}
        onChange={(e) => setEditor((s) => ({ ...s, secretValue: e.target.value }))}
        placeholder="substack.sid=…  (or paste the full Cookie header)"
        rows={3}
        autoComplete="off"
        spellCheck={false}
      />
    </div>

    <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 text-xs text-muted-foreground">
      <p className="mb-1 font-medium text-foreground">How to grab your session cookie</p>
      <ol className="list-decimal space-y-0.5 pl-4">
        <li>Open your Substack publication in a browser where you&apos;re signed in.</li>
        <li>
          Open DevTools → <strong>Application</strong> (Chrome) or{" "}
          <strong>Storage</strong> (Firefox) → <strong>Cookies</strong> →{" "}
          <code>https://yourname.substack.com</code>.
        </li>
        <li>
          Find <code>substack.sid</code>. Copy <em>just the value</em> and paste above,
          or paste the full <code>Cookie:</code> request header from the Network tab.
        </li>
      </ol>
      <p className="mt-2">
        We encrypt and store the cookie server-side. It expires on Substack&apos;s schedule
        — when dispatches start failing with <strong>401</strong>, paste a fresh value here
        and hit <strong>Test</strong>.
      </p>
    </div>
  </div>
);
